'use client';

import { ThemeToggle } from '@/components/common/ThemeToggle';
import Registration from '@/components/features/core/auth/Registration';
import { CDN_BASE_URL } from '@/lib/constants/cdn';
import ROUTES from '@/lib/constants/routeConfig';
import { useAuth } from '@/lib/hooks/useAuth';
import { cn } from '@/lib/utils';
import { useChangeLocale, useCurrentLocale, useI18n } from '@/locales/client';
import { Scale, UserCog } from 'lucide-react';
import Image from 'next/image';
import { useRouter } from 'next/navigation';
import { useEffect, useState } from 'react';
import { featureFlagsSelectors } from '@/lib/store/feature-flags.store';
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  CardFooter,
} from '@/components/common/layout/card';

export default function LoginPage() {
  const router = useRouter();
  const { status } = useAuth();
  const currentLocale = useCurrentLocale();
  const changeLocale = useChangeLocale();
  const [selectedCard, setSelectedCard] = useState('user');
  const [showRegistration, setShowRegistration] = useState(false);
  const t = useI18n();

  const cards = [
    {
      id: 'user',
      title: t('register.getLegalHelp'),
      description: t('register.getLegalHelpDescription'),
      icon: Scale,
    },
    {
      id: 'lawyer',
      title: t('register.joinAsLegalExpert'),
      description: t('register.joinAsLegalExpertDescription'),
      icon: UserCog,
    },
  ];

  const handleCardClick = (cardId: string) => {
    localStorage.setItem('selectedCard', cardId);
    setSelectedCard(cardId);
  };

  const handleContinue = () => {
    setShowRegistration(true);
  };

  useEffect(() => {
    localStorage.setItem('selectedCard', 'user');
    if (status === 'authenticated') {
      router.push(ROUTES.HOME);
    }
  }, [status, router]);

  return (
    <div className='bg-[#F9FAFB] dark:bg-background'>
      <div className='relative mx-auto max-w-[1200px]'>
        <div className='absolute right-4 top-4 flex items-center space-x-2'>
          <ThemeToggle />

          <div className='flex overflow-hidden rounded-md border border-[#E5E7EB] dark:border-gray-700'>
            <button
              onClick={() => changeLocale('en')}
              className={`px-3 py-1.5 text-sm font-medium transition-colors ${
                currentLocale === 'en'
                  ? 'bg-[#9147ff] bg-opacity-20 text-[#9147ff] dark:bg-purple-900/30 dark:text-purple-400'
                  : 'text-foreground hover:bg-gray-100 dark:hover:bg-gray-800'
              }`}
            >
              EN
            </button>
            <button
              onClick={() => changeLocale('fr')}
              className={`px-3 py-1.5 text-sm font-medium transition-colors ${
                currentLocale === 'fr'
                  ? 'bg-[#9147ff] bg-opacity-20 text-[#9147ff] dark:bg-purple-900/30 dark:text-purple-400'
                  : 'text-foreground hover:bg-gray-100 dark:hover:bg-gray-800'
              }`}
            >
              FR
            </button>
          </div>
        </div>
      </div>

      <div className='mx-auto grid min-h-dvh max-w-[1200px] grid-cols-1 gap-8 px-4 py-2 lg:grid-cols-2'>
        {/* <div className='flex h-full items-center justify-center pt-4 sm:pt-0'>
            <Card className='mx-auto w-full max-w-[500px] border border-border bg-card p-6 shadow-md dark:bg-background'>
              <CardHeader className='text-center'>
                <div className='flex flex-col items-center space-y-4'>
                  <div className='relative h-[88px] w-[88px]'>
                    <Image
                      fill
                      sizes='(max-width: 88px) 100vw, 88px'
                      style={{ objectFit: 'cover' }}
                      alt='Jaqln Logo'
                      priority
                      src={`${CDN_BASE_URL}/JAQLNLogo.png`}
                    />
                  </div>
                  <CardTitle className='text-2xl font-bold'>Sorry...</CardTitle>
                </div>
              </CardHeader>
              <CardContent className='space-y-4 text-center'>
                <p className='text-lg text-muted-foreground'>
                  We are not accepting new users at the moment. Please check
                  back later.
                </p>
              </CardContent>
              <CardFooter className='flex justify-center pt-4'>
                <p className='text-sm text-muted-foreground'>
                  {t('register.haveAnAccount')}{' '}
                  <a
                    href={ROUTES.AUTH.LOGIN}
                    className='text-primary underline hover:text-primary/80'
                  >
                    {t('register.signIn')}
                  </a>
                </p>
              </CardFooter>
            </Card>
          </div>
         */}

        {/* If need to block registration, comment out the following section and uncomment the one above */}
        <div className='flex h-full items-center justify-center pt-4 sm:pt-0'>
          {showRegistration ? (
            <Registration />
          ) : (
            <div className='flex w-full flex-col gap-4'>
              <div className='mb-8 flex flex-col gap-4'>
                <h2 className='text-4xl font-medium'>{t('register.joinUs')}</h2>
                <p className='text-lg font-light text-gray-600 dark:text-gray-400'>
                  {t('register.loginToAccessAccount')}
                </p>

                <div className='flex items-center gap-4'>
                  <div className='h-[4px] w-[76px] rounded-full bg-[#8E65FF]' />
                  <div className='h-[4px] w-[76px] rounded-full bg-[#E6E6EC]' />
                </div>
              </div>

              {/* Card Selectors */}

              <div className='flex flex-col gap-4'>
                {cards.map((card) => (
                  <div
                    key={card.id}
                    className={cn(
                      'flex cursor-pointer items-center gap-4 rounded-lg border-2 border-transparent bg-white px-4 py-6 transition-all duration-200 ease-in-out dark:bg-purple-900/20',
                      selectedCard === card.id
                        ? 'border-[#7846FF] bg-[#885CFF2B]'
                        : 'border-transparent'
                    )}
                    onClick={() => handleCardClick(card.id)}
                  >
                    <div
                      className={cn(
                        'flex h-12 w-12 flex-shrink-0 items-center justify-center rounded-full transition-colors duration-200',
                        selectedCard === card.id
                          ? 'bg-[#7846FF]'
                          : 'bg-gray-100 dark:bg-gray-800'
                      )}
                    >
                      {card.icon && (
                        <card.icon
                          className={cn(
                            'h-6 w-6 transition-colors duration-200',
                            selectedCard === card.id
                              ? 'text-white'
                              : 'text-[#384250] dark:text-gray-400'
                          )}
                        />
                      )}
                    </div>
                    <div>
                      <h3
                        className={cn(
                          'text-lg font-normal transition-colors duration-200',
                          selectedCard === card.id
                            ? 'text-[#7846FF]'
                            : 'text-[#384250] dark:text-gray-200'
                        )}
                      >
                        {card.title}
                      </h3>
                      <p className='text-sm font-light text-[#384250] dark:text-gray-400'>
                        {card.description}
                      </p>
                    </div>
                  </div>
                ))}

                <p className='text-sm font-light text-[#384250] dark:text-gray-400'>
                  {t('register.importantInfo')}
                </p>
              </div>
              {/* CTA Button */}
              <button
                className='mt-6 w-full rounded-lg bg-purple-500 px-4 py-3 text-white transition-colors hover:bg-purple-600'
                onClick={handleContinue}
              >
                {selectedCard === 'user'
                  ? t('register.getLegalHelp')
                  : t('register.joinAsLegalExpert')}
              </button>

              {/* Sign In Link */}
              <p className='mt-4 text-center text-sm text-gray-600 dark:text-gray-400'>
                {t('register.haveAnAccount')}{' '}
                <a
                  href={ROUTES.AUTH.LOGIN}
                  className='text-purple-500 hover:text-purple-600'
                >
                  {t('register.signIn')}
                </a>
              </p>
            </div>
          )}
        </div>
        <div className='hidden h-full items-center justify-center lg:flex'>
          <Image
            src={`${CDN_BASE_URL}/login-banner-${currentLocale}.png`}
            alt='Login Background'
            width={616}
            height={816}
            className='max-h-[calc(100vh-10rem)] rounded-lg border border-[#E5E7EB] object-cover dark:border-gray-700 dark:brightness-[0.85]'
            priority
          />
        </div>
      </div>
    </div>
  );
}
